import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import PolicyTab from '../../../components/common/PolicyTab';
import ModuleHeaderStrip from '../../../components/common/ModuleHeaderStrip';

const FEE_STATUS_BADGE = {
  paid:    { bs: 'success', label: 'Paid' },
  pending: { bs: 'warning', label: 'Pending' },
  overdue: { bs: 'danger',  label: 'Overdue' },
  waived:  { bs: 'secondary', label: 'Waived' },
};
const STATUS_BADGE = {
  active:        { bs: 'success', label: 'Active' },
  paused:        { bs: 'warning', label: 'Paused' },
  discontinued:  { bs: 'secondary', label: 'Discontinued' },
};
const ROUTE_STATUS_BADGE = {
  active:   { bs: 'success', label: 'Active' },
  inactive: { bs: 'secondary', label: 'Inactive' },
  archived: { bs: 'dark', label: 'Archived' },
};

const blankRoute = {
  code: '', name: '', description: '',
  vehicle: { number: '', model: '', capacity: 40, registrationNumber: '', fuelType: 'diesel' },
  driver:  { name: '', phone: '', license: '' },
  helper:  { name: '', phone: '' },
  defaultMonthlyFee: 0, academicYear: '', totalDistanceKm: 0,
};

// ─── Main ───────────────────────────────────────────────────────────────────
export default function TransportPage() {
  const { user } = useAuth();
  const role = user?.roleKey || '';
  const canEdit  = ['admin','manager','team_lead','faculty','teacher'].includes(role) || user?.userType === 'super_admin';
  const canAdmin = ['admin','manager'].includes(role) || user?.userType === 'super_admin';

  const [view, setView]               = useState('list'); // 'list' | 'detail' | 'policy'
  const [routes, setRoutes]           = useState([]);
  const [stats, setStats]             = useState({});
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [route, setRoute]             = useState(null);
  const [showCreate, setShowCreate]   = useState(false);
  const [createForm, setCreateForm]   = useState(blankRoute);
  const [saving, setSaving]           = useState(false);

  const [toast, setToast]             = useState(null);

  // ─── Load list + stats ────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search)       params.search = search;
      if (statusFilter) params.status = statusFilter;
      const [rs, st] = await Promise.all([
        api.get('/education/transport',       { params }),
        api.get('/education/transport/stats').catch(() => ({ data: { data: {} } })),
      ]);
      setRoutes(rs.data?.records || rs.data?.data || []);
      setStats(st.data?.data || st.data || {});
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed to load routes.' });
    }
    setLoading(false);
  }, [search, statusFilter]);

  useEffect(() => { if (view === 'list') load(); }, [view, load]);

  // ─── Open / refresh route detail ──────────────────────────────────────
  const openRoute = async (id) => {
    try {
      const r = await api.get(`/education/transport/${id}`);
      setRoute(r.data?.data || r.data);
      setView('detail');
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Failed to load route.' });
    }
  };
  const reloadRoute = async () => {
    if (!route?._id) return;
    const r = await api.get(`/education/transport/${route._id}`);
    setRoute(r.data?.data || r.data);
  };

  // ─── Create route ─────────────────────────────────────────────────────
  const create = async () => {
    if (!createForm.code || !createForm.name) {
      return setToast({ type: 'danger', msg: 'Code and name are required.' });
    }
    setSaving(true);
    try {
      const r = await api.post('/education/transport', createForm);
      setToast({ type: 'success', msg: 'Route created.' });
      setShowCreate(false);
      setCreateForm(blankRoute);
      load();
      // Open the newly created route for quick stop/student setup.
      const created = r.data?.data || r.data;
      if (created?._id) openRoute(created._id);
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Create failed.' });
    }
    setSaving(false);
  };

  const deleteRoute = async (r) => {
    if (!window.confirm(`Delete route ${r.code} (${r.name})?`)) return;
    try {
      await api.delete(`/education/transport/${r._id}`);
      setToast({ type: 'success', msg: 'Route deleted.' });
      load();
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Delete failed.' });
    }
  };

  return (
    <div>
      <PageHeader title="Transport / Bus Management" icon="bi-bus-front"
        subtitle="Routes, stops, vehicles, drivers and student assignments" />
      {toast && <AlertToast type={toast.type} message={toast.msg} show={true} onClose={() => setToast(null)} />}

      {view !== 'policy' && (
        <ModuleHeaderStrip
          icon="bi-bus-front"
          label="Transport"
          chips={[
            { label: 'Routes',     value: stats.routesCount   || 0 },
            { label: 'Active',     value: stats.activeRoutes  || 0 },
            { label: 'Students',   value: stats.totalStudents || 0 },
            { label: 'Capacity',   value: stats.totalCapacity || 0 },
            { label: 'Occupancy',  value: `${stats.occupancyPct || 0}%` },
            ...(stats.monthlyRevenue ? [{ label: 'Monthly Revenue', value: `₹${stats.monthlyRevenue.toLocaleString('en-IN')}` }] : []),
            ...(stats.overdueStudents > 0 ? [{ label: 'Fee Overdue', value: stats.overdueStudents }] : []),
          ]}
          variant="warning"
        />
      )}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item"><button className={`nav-link ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')}>
          <i className="bi bi-grid me-1"/>Routes
        </button></li>
        {route && view !== 'policy' && (
          <li className="nav-item"><button className={`nav-link ${view === 'detail' ? 'active' : ''}`} onClick={() => setView('detail')}>
            <i className="bi bi-geo-alt me-1"/>{route.code} · {route.name}
          </button></li>
        )}
        <li className="nav-item"><button className={`nav-link ${view === 'policy' ? 'active' : ''}`} onClick={() => setView('policy')}>
          <i className="bi bi-journal-text me-1"/>Policy & Manual
        </button></li>
      </ul>

      {view === 'list' && (
        <>
          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-2 align-items-end">
                <div className="col-md-4">
                  <label className="form-label small mb-1">Search</label>
                  <input className="form-control form-control-sm"
                    placeholder="Route code, name, vehicle no, driver…"
                    value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <div className="col-md-3">
                  <label className="form-label small mb-1">Status</label>
                  <select className="form-select form-select-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="col-md-5 text-end">
                  {canEdit && (
                    <button className="btn btn-primary btn-sm" onClick={() => { setCreateForm(blankRoute); setShowCreate(true); }}>
                      <i className="bi bi-plus-lg me-1"/>New Route
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4"><LoadingSpinner /></div>
          ) : routes.length === 0 ? (
            <div className="card">
              <div className="card-body text-center text-muted py-5">
                <i className="bi bi-bus-front fs-1 d-block mb-2"/>
                No routes configured yet. {canEdit && <span>Click <strong>New Route</strong> to add one.</span>}
              </div>
            </div>
          ) : (
            <div className="row g-3">
              {routes.map(r => {
                const sb = ROUTE_STATUS_BADGE[r.status] || ROUTE_STATUS_BADGE.active;
                const occ = r.occupancyPct || 0;
                return (
                  <div key={r._id} className="col-md-6 col-lg-4">
                    <div className="card h-100 shadow-sm" style={{ borderRadius: 12, cursor: 'pointer' }} onClick={() => openRoute(r._id)}>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <div className="fw-bold" style={{ fontSize: 18 }}>
                              <i className="bi bi-bus-front me-2 text-warning"/>{r.code}
                            </div>
                            <div className="small text-muted">{r.name}</div>
                          </div>
                          <span className={`badge bg-${sb.bs}`}>{sb.label}</span>
                        </div>
                        <div className="row g-2 small mt-2">
                          <div className="col-6">
                            <div className="text-muted">Vehicle</div>
                            <div className="fw-semibold">{r.vehicleNumber || '—'}</div>
                          </div>
                          <div className="col-6">
                            <div className="text-muted">Driver</div>
                            <div className="fw-semibold">{r.driverName || '—'}</div>
                          </div>
                          <div className="col-4">
                            <div className="text-muted">Stops</div>
                            <div className="fw-semibold">{r.stopsCount || 0}</div>
                          </div>
                          <div className="col-4">
                            <div className="text-muted">Students</div>
                            <div className="fw-semibold">{r.studentsCount || 0}</div>
                          </div>
                          <div className="col-4">
                            <div className="text-muted">Capacity</div>
                            <div className="fw-semibold">{r.vehicleCapacity || 0}</div>
                          </div>
                        </div>
                        <div className="mt-2">
                          <div className="d-flex justify-content-between small mb-1">
                            <span className="text-muted">Occupancy</span>
                            <span className="fw-semibold">{occ}%</span>
                          </div>
                          <div className="progress" style={{ height: 6 }}>
                            <div className={`progress-bar bg-${occ >= 90 ? 'danger' : occ >= 70 ? 'warning' : 'success'}`} style={{ width: `${occ}%` }}/>
                          </div>
                        </div>
                        {canAdmin && (
                          <div className="text-end mt-2">
                            <button className="btn btn-outline-danger btn-sm py-0 px-2" onClick={(e) => { e.stopPropagation(); deleteRoute(r); }}>
                              <i className="bi bi-trash"/>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* New route modal */}
          {showCreate && (
            <CreateRouteModal form={createForm} setForm={setCreateForm} saving={saving}
              onClose={() => setShowCreate(false)} onSubmit={create} />
          )}
        </>
      )}

      {view === 'detail' && route && (
        <RouteDetailView route={route} canEdit={canEdit} canAdmin={canAdmin} reload={reloadRoute} setToast={setToast}
          onUpdated={(updated) => setRoute(updated)} />
      )}

      {view === 'policy' && <PolicyTab moduleKey="transport" label="Transport Module — Manual & Policy" />}
    </div>
  );
}

// ─── Create Route Modal ─────────────────────────────────────────────────────
function CreateRouteModal({ form, setForm, saving, onClose, onSubmit }) {
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setVeh = (k, v) => setForm(p => ({ ...p, vehicle: { ...p.vehicle, [k]: v } }));
  const setDrv = (k, v) => setForm(p => ({ ...p, driver:  { ...p.driver,  [k]: v } }));

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title"><i className="bi bi-bus-front me-2"/>New Route</h5>
            <button className="btn-close" onClick={onClose}/>
          </div>
          <div className="modal-body">
            <h6 className="text-muted small text-uppercase">Identity</h6>
            <div className="row g-2 mb-3">
              <div className="col-md-3">
                <label className="form-label small">Code <span className="text-danger">*</span></label>
                <input className="form-control form-control-sm" placeholder="RT-01" value={form.code} onChange={e => setF('code', e.target.value)} />
              </div>
              <div className="col-md-9">
                <label className="form-label small">Name <span className="text-danger">*</span></label>
                <input className="form-control form-control-sm" placeholder="e.g. Anna Nagar Loop" value={form.name} onChange={e => setF('name', e.target.value)} />
              </div>
              <div className="col-12">
                <label className="form-label small">Description</label>
                <input className="form-control form-control-sm" value={form.description} onChange={e => setF('description', e.target.value)} />
              </div>
            </div>

            <h6 className="text-muted small text-uppercase">Vehicle</h6>
            <div className="row g-2 mb-3">
              <div className="col-md-4">
                <label className="form-label small">Number</label>
                <input className="form-control form-control-sm" placeholder="TN-09 AB 1234" value={form.vehicle.number} onChange={e => setVeh('number', e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label small">Model</label>
                <input className="form-control form-control-sm" placeholder="Tata 909 LP" value={form.vehicle.model} onChange={e => setVeh('model', e.target.value)} />
              </div>
              <div className="col-md-2">
                <label className="form-label small">Capacity</label>
                <input type="number" min="1" className="form-control form-control-sm" value={form.vehicle.capacity} onChange={e => setVeh('capacity', parseInt(e.target.value) || 0)} />
              </div>
              <div className="col-md-2">
                <label className="form-label small">Fuel</label>
                <select className="form-select form-select-sm" value={form.vehicle.fuelType} onChange={e => setVeh('fuelType', e.target.value)}>
                  <option value="diesel">Diesel</option>
                  <option value="petrol">Petrol</option>
                  <option value="cng">CNG</option>
                  <option value="electric">Electric</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <h6 className="text-muted small text-uppercase">Driver</h6>
            <div className="row g-2 mb-3">
              <div className="col-md-5">
                <label className="form-label small">Name</label>
                <input className="form-control form-control-sm" value={form.driver.name} onChange={e => setDrv('name', e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label small">Phone</label>
                <input className="form-control form-control-sm" value={form.driver.phone} onChange={e => setDrv('phone', e.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label small">License No.</label>
                <input className="form-control form-control-sm" value={form.driver.license} onChange={e => setDrv('license', e.target.value)} />
              </div>
            </div>

            <h6 className="text-muted small text-uppercase">Operations</h6>
            <div className="row g-2">
              <div className="col-md-3">
                <label className="form-label small">Default Monthly Fee (₹)</label>
                <input type="number" className="form-control form-control-sm" value={form.defaultMonthlyFee} onChange={e => setF('defaultMonthlyFee', parseFloat(e.target.value) || 0)} />
              </div>
              <div className="col-md-3">
                <label className="form-label small">Distance (km)</label>
                <input type="number" className="form-control form-control-sm" value={form.totalDistanceKm} onChange={e => setF('totalDistanceKm', parseFloat(e.target.value) || 0)} />
              </div>
              <div className="col-md-3">
                <label className="form-label small">Academic Year</label>
                <input className="form-control form-control-sm" placeholder="2026-27" value={form.academicYear} onChange={e => setF('academicYear', e.target.value)} />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={onSubmit} disabled={saving}>
              {saving && <span className="spinner-border spinner-border-sm me-1"/>}Create Route
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Detail View ───────────────────────────────────────────────────────────
function RouteDetailView({ route, canEdit, canAdmin, reload, setToast, onUpdated }) {
  const [tab, setTab] = useState('stops'); // 'stops' | 'students' | 'metadata'

  // Inline edit for route metadata
  const [meta, setMeta] = useState({
    code: route.code, name: route.name, description: route.description,
    vehicle: { ...route.vehicle }, driver: { ...route.driver }, helper: { ...route.helper },
    defaultMonthlyFee: route.defaultMonthlyFee, academicYear: route.academicYear,
    totalDistanceKm: route.totalDistanceKm, status: route.status,
  });
  useEffect(() => {
    setMeta({
      code: route.code, name: route.name, description: route.description,
      vehicle: { ...route.vehicle }, driver: { ...route.driver }, helper: { ...route.helper },
      defaultMonthlyFee: route.defaultMonthlyFee, academicYear: route.academicYear,
      totalDistanceKm: route.totalDistanceKm, status: route.status,
    });
  }, [route._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMeta = async () => {
    try {
      const r = await api.put(`/education/transport/${route._id}`, meta);
      onUpdated(r.data?.data || r.data);
      setToast({ type: 'success', msg: 'Route saved.' });
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Save failed.' });
    }
  };

  return (
    <>
      {/* Banner */}
      <div className="card mb-3" style={{ background: 'linear-gradient(135deg, #fef3c7, #fed7aa)', border: 'none' }}>
        <div className="card-body py-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h5 className="mb-1 fw-bold"><i className="bi bi-bus-front me-2"/>{route.code} · {route.name}</h5>
              <div className="small text-muted">
                Vehicle: <strong>{route.vehicle?.number || '—'}</strong> ({route.vehicle?.capacity || 0} seats)
                <span className="mx-2">·</span>
                Driver: <strong>{route.driver?.name || '—'}</strong>
                {route.driver?.phone && <span className="ms-1">({route.driver.phone})</span>}
              </div>
              <div className="small text-muted mt-1">
                <span className="badge bg-light text-dark border me-1">{(route.stops || []).length} stops</span>
                <span className="badge bg-light text-dark border me-1">
                  {(route.students || []).filter(s => s.status === 'active').length} active students
                </span>
                {route.totalDistanceKm > 0 && <span className="badge bg-light text-dark border me-1">{route.totalDistanceKm} km</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ul className="nav nav-pills mb-3">
        <li className="nav-item"><button className={`nav-link ${tab === 'stops' ? 'active' : ''}`} onClick={() => setTab('stops')}>
          <i className="bi bi-geo-alt me-1"/>Stops
        </button></li>
        <li className="nav-item ms-2"><button className={`nav-link ${tab === 'students' ? 'active' : ''}`} onClick={() => setTab('students')}>
          <i className="bi bi-people me-1"/>Students
        </button></li>
        <li className="nav-item ms-2"><button className={`nav-link ${tab === 'metadata' ? 'active' : ''}`} onClick={() => setTab('metadata')}>
          <i className="bi bi-gear me-1"/>Vehicle, Driver & Settings
        </button></li>
      </ul>

      {tab === 'stops'    && <StopsTab    route={route} canEdit={canEdit} reload={reload} setToast={setToast}/>}
      {tab === 'students' && <StudentsTab route={route} canEdit={canEdit} reload={reload} setToast={setToast}/>}
      {tab === 'metadata' && <MetadataTab route={route} meta={meta} setMeta={setMeta} canEdit={canEdit} onSave={saveMeta}/>}
    </>
  );
}

// ─── Stops Tab ──────────────────────────────────────────────────────────────
function StopsTab({ route, canEdit, reload, setToast }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm]     = useState({ name: '', address: '', pickupTime: '', dropTime: '', notes: '' });

  const add = async () => {
    if (!form.name.trim()) return setToast({ type: 'danger', msg: 'Stop name is required.' });
    try {
      await api.post(`/education/transport/${route._id}/stops`, form);
      setToast({ type: 'success', msg: 'Stop added.' });
      setForm({ name: '', address: '', pickupTime: '', dropTime: '', notes: '' });
      setAdding(false);
      reload();
    } catch (e) { setToast({ type: 'danger', msg: e.response?.data?.message || 'Add failed.' }); }
  };
  const remove = async (id) => {
    if (!window.confirm('Remove this stop? Students assigned to it will lose their stop assignment.')) return;
    try {
      await api.delete(`/education/transport/${route._id}/stops/${id}`);
      reload();
    } catch (e) { setToast({ type: 'danger', msg: e.response?.data?.message || 'Remove failed.' }); }
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <span className="fw-semibold"><i className="bi bi-geo-alt me-1 text-warning"/>Stops on this route ({(route.stops || []).length})</span>
        {canEdit && (
          <button className="btn btn-primary btn-sm" onClick={() => setAdding(s => !s)}>
            <i className="bi bi-plus-lg me-1"/>{adding ? 'Cancel' : 'Add Stop'}
          </button>
        )}
      </div>
      {adding && (
        <div className="card-body border-bottom" style={{ background: '#fffaf0' }}>
          <div className="row g-2">
            <div className="col-md-4">
              <label className="form-label small">Stop Name <span className="text-danger">*</span></label>
              <input className="form-control form-control-sm" placeholder="e.g. Anna Nagar Tower"
                value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="col-md-4">
              <label className="form-label small">Address</label>
              <input className="form-control form-control-sm" value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Pickup</label>
              <input type="time" className="form-control form-control-sm" value={form.pickupTime} onChange={e => setForm(p => ({ ...p, pickupTime: e.target.value }))} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Drop</label>
              <input type="time" className="form-control form-control-sm" value={form.dropTime} onChange={e => setForm(p => ({ ...p, dropTime: e.target.value }))} />
            </div>
            <div className="col-12">
              <label className="form-label small">Notes</label>
              <input className="form-control form-control-sm" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
            <div className="col-12 text-end">
              <button className="btn btn-success btn-sm" onClick={add}><i className="bi bi-check-lg me-1"/>Add Stop</button>
            </div>
          </div>
        </div>
      )}
      <div className="card-body p-0">
        {(route.stops || []).length === 0 ? (
          <div className="text-center text-muted py-4">No stops added yet.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 50 }}>#</th>
                  <th>Stop Name</th>
                  <th>Address</th>
                  <th style={{ width: 100 }}>Pickup</th>
                  <th style={{ width: 100 }}>Drop</th>
                  <th>Notes</th>
                  {canEdit && <th style={{ width: 60 }}></th>}
                </tr>
              </thead>
              <tbody>
                {(route.stops || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)).map((s, i) => (
                  <tr key={s._id}>
                    <td className="fw-bold">{s.order || i + 1}</td>
                    <td>{s.name}</td>
                    <td className="small text-muted">{s.address || '—'}</td>
                    <td>{s.pickupTime || '—'}</td>
                    <td>{s.dropTime || '—'}</td>
                    <td className="small text-muted">{s.notes || '—'}</td>
                    {canEdit && (
                      <td>
                        <button className="btn btn-outline-danger btn-sm py-0 px-2" onClick={() => remove(s._id)}>
                          <i className="bi bi-trash"/>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Students Tab ───────────────────────────────────────────────────────────
function StudentsTab({ route, canEdit, reload, setToast }) {
  const [showAssign, setShowAssign] = useState(false);
  const [students, setStudents]     = useState([]);
  const [search, setSearch]         = useState('');
  const [assignForm, setAssignForm] = useState({ studentRecordId: '', stopId: '', monthlyFee: route.defaultMonthlyFee || 0 });

  // Load students from student_management for the assignment picker.
  useEffect(() => {
    if (!showAssign) return;
    api.get('/records/student_management', { params: { limit: 500 } })
      .then(r => setStudents(r.data?.records || []))
      .catch(() => setStudents([]));
  }, [showAssign]);

  const assigned = route.students || [];
  const assignedIds = new Set(assigned.map(s => String(s.studentRecordId || '')));

  const eligible = students.filter(s => {
    if (assignedIds.has(String(s._id))) return false;   // already on this route
    const d = s.recordData || {};
    if (!search) return true;
    const q = search.toLowerCase();
    const name = `${d.first_name || d.firstName || ''} ${d.last_name || d.lastName || ''}`.toLowerCase();
    return name.includes(q) || (d.admission_number || '').toLowerCase().includes(q);
  });

  const assign = async () => {
    if (!assignForm.studentRecordId) return setToast({ type: 'danger', msg: 'Pick a student.' });
    const stu = students.find(s => s._id === assignForm.studentRecordId);
    if (!stu) return;
    const d = stu.recordData || {};
    try {
      await api.post(`/education/transport/${route._id}/students`, {
        studentRecordId: stu._id,
        studentName: `${d.first_name || d.firstName || ''} ${d.last_name || d.lastName || ''}`.trim() || d.full_name || d.name || 'Student',
        admissionNumber: d.admission_number || '',
        classGrade: d.class_grade || '',
        section: d.section || '',
        parentPhone: d.parent_phone || '',
        stopId: assignForm.stopId,
        monthlyFee: assignForm.monthlyFee,
      });
      setToast({ type: 'success', msg: 'Student assigned.' });
      setAssignForm({ studentRecordId: '', stopId: '', monthlyFee: route.defaultMonthlyFee || 0 });
      reload();
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Assignment failed.' });
    }
  };
  const updateAssignment = async (entryId, patch) => {
    try {
      await api.patch(`/education/transport/${route._id}/students/${entryId}`, patch);
      reload();
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Update failed.' });
    }
  };
  const removeAssignment = async (entryId) => {
    if (!window.confirm('Remove this student from the route?')) return;
    try {
      await api.delete(`/education/transport/${route._id}/students/${entryId}`);
      reload();
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Remove failed.' });
    }
  };

  const stopName = (id) => (route.stops || []).find(s => s._id === id)?.name || '—';

  return (
    <>
      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span className="fw-semibold">
            <i className="bi bi-people me-1 text-primary"/>
            Students on this route ({assigned.length})
            {route.vehicle?.capacity && (
              <span className="badge bg-light text-dark border ms-2">
                {assigned.filter(s => s.status === 'active').length} / {route.vehicle.capacity} capacity
              </span>
            )}
          </span>
          {canEdit && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowAssign(s => !s)}>
              <i className="bi bi-person-plus me-1"/>{showAssign ? 'Cancel' : 'Assign Student'}
            </button>
          )}
        </div>
        {showAssign && (
          <div className="card-body border-bottom" style={{ background: '#f0f9ff' }}>
            <input className="form-control form-control-sm mb-2" placeholder="Search student by name or admission no…"
              value={search} onChange={e => setSearch(e.target.value)} />
            <div className="row g-2">
              <div className="col-md-5">
                <label className="form-label small">Student <span className="text-danger">*</span></label>
                <select className="form-select form-select-sm" size={5}
                  value={assignForm.studentRecordId} onChange={e => setAssignForm(p => ({ ...p, studentRecordId: e.target.value }))}>
                  {eligible.length === 0 && <option disabled>No eligible students.</option>}
                  {eligible.map(s => {
                    const d = s.recordData || {};
                    const name = `${d.first_name || d.firstName || ''} ${d.last_name || d.lastName || ''}`.trim() || '(unnamed)';
                    const cls = [d.class_grade, d.section].filter(Boolean).join(' — ');
                    return <option key={s._id} value={s._id}>{name}{d.admission_number ? ` · ${d.admission_number}` : ''}{cls ? ` · ${cls}` : ''}</option>;
                  })}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label small">Boarding Stop</label>
                <select className="form-select form-select-sm" value={assignForm.stopId} onChange={e => setAssignForm(p => ({ ...p, stopId: e.target.value }))}>
                  <option value="">— None / Not yet —</option>
                  {(route.stops || []).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small">Monthly Fee (₹)</label>
                <input type="number" className="form-control form-control-sm"
                  value={assignForm.monthlyFee} onChange={e => setAssignForm(p => ({ ...p, monthlyFee: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div className="col-12 text-end">
                <button className="btn btn-success btn-sm" onClick={assign}><i className="bi bi-check-lg me-1"/>Assign</button>
              </div>
            </div>
          </div>
        )}
        <div className="card-body p-0">
          {assigned.length === 0 ? (
            <div className="text-center text-muted py-4">No students assigned yet.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Student</th>
                    <th>Class</th>
                    <th>Stop</th>
                    <th>Parent Phone</th>
                    <th>Fee (₹)</th>
                    <th>Fee Status</th>
                    <th>Status</th>
                    {canEdit && <th style={{ width: 60 }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {assigned.map(s => {
                    const fb = FEE_STATUS_BADGE[s.feeStatus] || FEE_STATUS_BADGE.pending;
                    const sb = STATUS_BADGE[s.status] || STATUS_BADGE.active;
                    return (
                      <tr key={s._id}>
                        <td>
                          <div className="fw-semibold">{s.studentName}</div>
                          {s.admissionNumber && <div className="text-muted small">{s.admissionNumber}</div>}
                        </td>
                        <td>{[s.classGrade, s.section].filter(Boolean).join(' — ') || '—'}</td>
                        <td>
                          {canEdit ? (
                            <select className="form-select form-select-sm" value={s.stopId || ''}
                              onChange={e => updateAssignment(s._id, { stopId: e.target.value })}>
                              <option value="">— None —</option>
                              {(route.stops || []).map(st => <option key={st._id} value={st._id}>{st.name}</option>)}
                            </select>
                          ) : stopName(s.stopId)}
                        </td>
                        <td>{s.parentPhone || '—'}</td>
                        <td>{canEdit ? (
                          <input type="number" className="form-control form-control-sm" style={{ width: 90 }}
                            value={s.monthlyFee} onBlur={e => updateAssignment(s._id, { monthlyFee: parseFloat(e.target.value) || 0 })}
                            onChange={() => {}} defaultValue={s.monthlyFee} />
                        ) : `₹${s.monthlyFee || 0}`}</td>
                        <td>
                          {canEdit ? (
                            <select className="form-select form-select-sm" value={s.feeStatus} onChange={e => updateAssignment(s._id, { feeStatus: e.target.value })}>
                              {Object.entries(FEE_STATUS_BADGE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select>
                          ) : <span className={`badge bg-${fb.bs}`}>{fb.label}</span>}
                        </td>
                        <td>
                          {canEdit ? (
                            <select className="form-select form-select-sm" value={s.status} onChange={e => updateAssignment(s._id, { status: e.target.value })}>
                              {Object.entries(STATUS_BADGE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select>
                          ) : <span className={`badge bg-${sb.bs}`}>{sb.label}</span>}
                        </td>
                        {canEdit && (
                          <td>
                            <button className="btn btn-outline-danger btn-sm py-0 px-2" onClick={() => removeAssignment(s._id)}>
                              <i className="bi bi-trash"/>
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Metadata Tab ───────────────────────────────────────────────────────────
function MetadataTab({ route, meta, setMeta, canEdit, onSave }) {
  const setVeh = (k, v) => setMeta(p => ({ ...p, vehicle: { ...p.vehicle, [k]: v } }));
  const setDrv = (k, v) => setMeta(p => ({ ...p, driver:  { ...p.driver,  [k]: v } }));
  const setHlp = (k, v) => setMeta(p => ({ ...p, helper:  { ...p.helper,  [k]: v } }));

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <span className="fw-semibold"><i className="bi bi-gear me-1"/>Vehicle, Driver & Settings</span>
        {canEdit && <button className="btn btn-success btn-sm" onClick={onSave}><i className="bi bi-save me-1"/>Save Changes</button>}
      </div>
      <div className="card-body">
        <h6 className="text-muted small text-uppercase">Identity</h6>
        <div className="row g-2 mb-3">
          <div className="col-md-3"><label className="form-label small">Code</label><input className="form-control form-control-sm" value={meta.code} onChange={e => setMeta(p => ({ ...p, code: e.target.value }))} disabled={!canEdit} /></div>
          <div className="col-md-6"><label className="form-label small">Name</label><input className="form-control form-control-sm" value={meta.name} onChange={e => setMeta(p => ({ ...p, name: e.target.value }))} disabled={!canEdit} /></div>
          <div className="col-md-3"><label className="form-label small">Status</label>
            <select className="form-select form-select-sm" value={meta.status} onChange={e => setMeta(p => ({ ...p, status: e.target.value }))} disabled={!canEdit}>
              <option value="active">Active</option><option value="inactive">Inactive</option><option value="archived">Archived</option>
            </select></div>
          <div className="col-12"><label className="form-label small">Description</label><input className="form-control form-control-sm" value={meta.description} onChange={e => setMeta(p => ({ ...p, description: e.target.value }))} disabled={!canEdit} /></div>
        </div>
        <h6 className="text-muted small text-uppercase">Vehicle</h6>
        <div className="row g-2 mb-3">
          <div className="col-md-3"><label className="form-label small">Number</label><input className="form-control form-control-sm" value={meta.vehicle.number || ''} onChange={e => setVeh('number', e.target.value)} disabled={!canEdit} /></div>
          <div className="col-md-3"><label className="form-label small">Model</label><input className="form-control form-control-sm" value={meta.vehicle.model || ''} onChange={e => setVeh('model', e.target.value)} disabled={!canEdit} /></div>
          <div className="col-md-2"><label className="form-label small">Capacity</label><input type="number" className="form-control form-control-sm" value={meta.vehicle.capacity || 0} onChange={e => setVeh('capacity', parseInt(e.target.value) || 0)} disabled={!canEdit} /></div>
          <div className="col-md-2"><label className="form-label small">Fuel</label>
            <select className="form-select form-select-sm" value={meta.vehicle.fuelType || 'diesel'} onChange={e => setVeh('fuelType', e.target.value)} disabled={!canEdit}>
              <option value="diesel">Diesel</option><option value="petrol">Petrol</option><option value="cng">CNG</option><option value="electric">Electric</option><option value="other">Other</option>
            </select></div>
          <div className="col-md-2"><label className="form-label small">Reg. No.</label><input className="form-control form-control-sm" value={meta.vehicle.registrationNumber || ''} onChange={e => setVeh('registrationNumber', e.target.value)} disabled={!canEdit} /></div>
        </div>
        <h6 className="text-muted small text-uppercase">Driver</h6>
        <div className="row g-2 mb-3">
          <div className="col-md-4"><label className="form-label small">Name</label><input className="form-control form-control-sm" value={meta.driver.name || ''} onChange={e => setDrv('name', e.target.value)} disabled={!canEdit} /></div>
          <div className="col-md-4"><label className="form-label small">Phone</label><input className="form-control form-control-sm" value={meta.driver.phone || ''} onChange={e => setDrv('phone', e.target.value)} disabled={!canEdit} /></div>
          <div className="col-md-4"><label className="form-label small">License No.</label><input className="form-control form-control-sm" value={meta.driver.license || ''} onChange={e => setDrv('license', e.target.value)} disabled={!canEdit} /></div>
        </div>
        <h6 className="text-muted small text-uppercase">Helper / Conductor</h6>
        <div className="row g-2 mb-3">
          <div className="col-md-6"><label className="form-label small">Name</label><input className="form-control form-control-sm" value={meta.helper.name || ''} onChange={e => setHlp('name', e.target.value)} disabled={!canEdit} /></div>
          <div className="col-md-6"><label className="form-label small">Phone</label><input className="form-control form-control-sm" value={meta.helper.phone || ''} onChange={e => setHlp('phone', e.target.value)} disabled={!canEdit} /></div>
        </div>
        <h6 className="text-muted small text-uppercase">Operations</h6>
        <div className="row g-2">
          <div className="col-md-3"><label className="form-label small">Default Monthly Fee (₹)</label><input type="number" className="form-control form-control-sm" value={meta.defaultMonthlyFee || 0} onChange={e => setMeta(p => ({ ...p, defaultMonthlyFee: parseFloat(e.target.value) || 0 }))} disabled={!canEdit} /></div>
          <div className="col-md-3"><label className="form-label small">Distance (km)</label><input type="number" className="form-control form-control-sm" value={meta.totalDistanceKm || 0} onChange={e => setMeta(p => ({ ...p, totalDistanceKm: parseFloat(e.target.value) || 0 }))} disabled={!canEdit} /></div>
          <div className="col-md-3"><label className="form-label small">Academic Year</label><input className="form-control form-control-sm" value={meta.academicYear || ''} onChange={e => setMeta(p => ({ ...p, academicYear: e.target.value }))} disabled={!canEdit} /></div>
        </div>
      </div>
    </div>
  );
}
